import type { UseMutationOptions } from "@tanstack/react-query";
import type { Client, FetchResponse, MaybeOptionalInit } from "openapi-fetch";
import type { HttpMethod, MediaType, PathsWithMethod } from "openapi-typescript-helpers";

type MutationKey<TMethod extends HttpMethod, TPath> = readonly [TMethod, TPath];

type MutationOptionsFunction<TPaths extends Record<string, Record<HttpMethod, {}>>, TMedia extends MediaType> = <
    TMethod extends HttpMethod,
    TPath extends PathsWithMethod<TPaths, TMethod>,
    TInit extends MaybeOptionalInit<TPaths[TPath], TMethod>,
    TResponse extends Required<FetchResponse<TPaths[TPath][TMethod], TInit, TMedia>>,
    TOptions extends Omit<
        UseMutationOptions<TResponse["data"], TResponse["error"], TInit>,
        "mutationKey" | "mutationFn"
    >
>(
    method: TMethod,
    path: TPath,
    options?: TOptions
) => UseMutationOptions<TResponse["data"], TResponse["error"], TInit> & { mutationKey: MutationKey<TMethod, TPath> };

type ClientMethod = (path: PropertyKey, init: unknown) => Promise<{ data: unknown; error: unknown }>;

/**
 * Backports openapi-react-query's missing `mutationOptions` helper (see openapi-ts PR #2487): given an
 * openapi-fetch client, returns a positional `mutationOptions(method, path, options?)` factory that mirrors
 * the `queryOptions` helper — auto `mutationKey` of `[method, path]` and a typed `mutationFn` calling the client.
 */
export const createMutationOptions = <TPaths extends {}, TMedia extends MediaType = MediaType>(
    client: Client<TPaths, TMedia>
): MutationOptionsFunction<TPaths, TMedia> =>
    ((method, path, options) => ({
        mutationKey: [method, path],
        mutationFn: async (init: unknown) => {
            // Indexing `Client` by a generic method yields a union of differently-typed overloads
            // that can't be called with a union argument — dynamic dispatch needs this cast.
            const fn = (client as unknown as Record<string, ClientMethod>)[method.toUpperCase()];
            const { data, error } = await fn(path, init);
            if (error) {
                throw error;
            }

            return data;
        },
        ...options
        // Bridges the concrete implementation to the generic signature above; `TResponse["data"]` can't
        // be derived from the runtime body, so this can't be proven structurally.
    })) as MutationOptionsFunction<TPaths, TMedia>;
